import { ChangeEvent, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { PalmettoOptionGroup, PalmettoCheckboxGroupProps } from '@palmetto/dsm-components';

type Props = Omit<PalmettoCheckboxGroupProps, 'onChange' | 'value'> & {
  onChange?: PalmettoCheckboxGroupProps['onChange'];
  value?: PalmettoCheckboxGroupProps['value'];
};

export const OptionGroup = ({ name, onChange, value, options, ...restProps }: Props) => {
  const formContext = useFormContext();

  const defaultValue = useMemo(() => {
    const initialSelected = value ?? [];
    return Object.fromEntries(options.map((opt) => [opt.value, initialSelected.includes(String(opt.value))]));
  }, [options, value]);

  const { field } = useController({
    name,
    control: formContext?.control,
    defaultValue,
  });

  const selectedValues = useMemo(
    () => Object.entries(field.value ?? {}).filter(([, selected]) => selected).map(([key]) => key),
    [field.value],
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedValue = e.target.value;
    field.onChange({ ...field.value, [selectedValue]: !field.value[selectedValue] });
    onChange?.(e);
  };

  return (
    <PalmettoOptionGroup
      {...restProps}
      options={options}
      name={name}
      value={selectedValues}
      onChange={handleChange}
    />
  );
};
